import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CalculateTaxApplicationErrors,
  CalculateTaxOptions,
  CalculateTaxResponse,
  ListTaxCalculatorsResponse,
  calculateTax as universalCalculateTax,
  listTaxCalculators as universalListTaxCalculators,
} from './gateways-billing-v1-tax-calculation-gateway-tax-calculation.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/billing' };

export function calculateTax(httpClient: HttpClient): CalculateTaxSignature {
  return (
    options?: NonNullablePaths<
      CalculateTaxOptions,
      | `addresses`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.addressIndex`
      | `lineItems.${number}.price`,
      4
    >
  ) =>
    universalCalculateTax(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateTaxSignature {
  /**
   * Calculates tax for the provided line items.
   *
   * Tax is calculated for each line item based on the tax region
   * that corresponds to the address provided in `lineItems.addressIndex` and the tax group in `taxGroupId`. If no tax region is found for
   * the line item's address then no tax will be calculated for this line item.
   *
   * The tax is calculated by a tax calculator app installed on the site.
   * Call List Tax Calculators to see which tax calculators are available.
   * To provide your own tax calculations, use the Tax Calculation service plugin.
   *
   * The breakdown of calculated tax returned, includes:
   * + `taxSummary`: The overall total tax calculated.
   * + `taxSummary.aggregatedTaxBreakdown`: The total tax calculated for each jurisdiction.
   * + `lineItemTaxDetails.taxSummary`: The total tax calculated for each line item.
   * + `lineItemTaxDetails.taxBreakdown`: The tax calculated for each line item in each jurisdiction.
   * @param - Calculate tax options.
   */
  (
    options?: NonNullablePaths<
      CalculateTaxOptions,
      | `addresses`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.addressIndex`
      | `lineItems.${number}.price`,
      4
    >
  ): Promise<
    NonNullablePaths<
      CalculateTaxResponse,
      | `currency`
      | `addresses`
      | `taxSummary.totalAmount`
      | `taxSummary.totalTax`
      | `taxSummary.totalTaxableAmount`
      | `taxSummary.totalTaxIncludedInPrice`
      | `taxSummary.aggregatedTaxBreakdown`
      | `taxSummary.aggregatedTaxBreakdown.${number}.taxName`
      | `taxSummary.aggregatedTaxBreakdown.${number}.taxType`
      | `taxSummary.aggregatedTaxBreakdown.${number}.jurisdiction`
      | `taxSummary.aggregatedTaxBreakdown.${number}.jurisdictionType`
      | `taxSummary.aggregatedTaxBreakdown.${number}.rate`
      | `taxSummary.aggregatedTaxBreakdown.${number}.aggregatedTaxAmount`
      | `lineItemTaxDetails`
      | `lineItemTaxDetails.${number}._id`
      | `lineItemTaxDetails.${number}.quantity`
      | `lineItemTaxDetails.${number}.taxSummary.taxAmount`
      | `lineItemTaxDetails.${number}.taxSummary.taxableAmount`
      | `lineItemTaxDetails.${number}.addressIndex.singleAddress`
      | `lineItemTaxDetails.${number}.addressIndex.multipleAddresses.origin`
      | `lineItemTaxDetails.${number}.addressIndex.multipleAddresses.destination`
      | `lineItemTaxDetails.${number}.taxIncludedInPrice`
      | `errors`
      | `errors.${number}.code`
      | `errors.${number}.description`,
      6
    > & {
      __applicationErrorsType?: CalculateTaxApplicationErrors;
    }
  >;
}

export function listTaxCalculators(
  httpClient: HttpClient
): ListTaxCalculatorsSignature {
  return () =>
    universalListTaxCalculators(
      // @ts-ignore
      { httpClient }
    );
}

interface ListTaxCalculatorsSignature {
  /**
   * Retrieves a list of installed tax calculators.
   *
   * Wix uses these calculators to calculate tax.
   */
  (): Promise<
    NonNullablePaths<
      ListTaxCalculatorsResponse,
      | `taxCalculatorDetails`
      | `taxCalculatorDetails.${number}.appId`
      | `taxCalculatorDetails.${number}.displayName`,
      4
    >
  >;
}

export {
  Address,
  AddressIndex,
  AddressIndexAddressIndexOptionsOneOf,
  AggregateTaxBreakdownsRequest,
  AggregateTaxBreakdownsResponse,
  AggregatedTaxBreakdown,
  ApplicationError,
  CalculateTaxOptions,
  CalculateTaxRequest,
  CalculateTaxResponse,
  ExemptBy,
  ExemptCalculationDetails,
  ExemptCalculationDetailsExemptByOptionsOneOf,
  JurisdictionType,
  LineItem,
  LineItemTaxDetails,
  LineItemTaxSummary,
  ListTaxCalculatorsRequest,
  ListTaxCalculatorsResponse,
  MultipleAddresses,
  TaxBreakdown,
  TaxCalculationGateway,
  TaxCalculatorDetails,
  TaxSummary,
} from './gateways-billing-v1-tax-calculation-gateway-tax-calculation.universal.js';
